


interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface ingredients {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    // ingredients: ingredients;
    // treatmentAreas1: TreatmentArea[];
    // treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const painmanagement: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} =
{
    "en": {
        "url": "pain-management",
        "meta": {
            "title": "Pain Management in Dubai | Elite Body Home",
            "description": "Comprehensive pain management solutions for chronic pain, injuries, and post-surgical recovery. Advanced treatments tailored to your specific needs.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/pain-management/",
            "og": {
                "title": "Pain Management in Dubai | Elite Body Home",
                "description": "Expert pain relief solutions for improved quality of life",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Pain-Management-Dubai.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Pain Management"],
        "hero": {
            "title": "Pain Management",
            "subtitle": "Professional Treatment to Relieve Pain and Improve Quality of Life",
            "description": "Pain management is essential to healthcare and it is all about alleviating pain, ensuring better quality of life, and enabling mobility and activity. This is true whether you're suffering from chronic pain, pain as a result of injury, or pain after surgery, and whether you are a patient seeing a doctor, you work in medicine, or something in-between; expert pain management can make a huge difference in your day-to-day life."
        },
        "howItWorks": {
            "title": "What Is Pain Management?",
            "description": "Pain medicine is a subspecialty of medicine that is all about relieving and controlling pain to help improve your physical health and mental well-being. Using a holistic approach, our knowledgeable staff utilizes a comprehensive range of advanced medical treatments, individualized care plans, and therapeutic techniques to help control and relieve pain.",

        },
        "procedureSteps": {
            "heading": "Our Pain Management Techniques Procedure",
            "para": "At Elite Body Home, we employ an array of state-of-the-art techniques to deliver customized relief in accordance to each patient's illness:",
            "steps": [
                {
                    "title": "Medications",
                    "desc": "These involve over-the-counter pain relievers, prescription pills such as muscle relaxants or opioids, and anti inflammation medicines.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Injections",
                    "desc": "Corticosteroid injections, nerve blocks or trigger point injections to decrease inflammation and pain in specific areas.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Physical Therapy",
                    "desc": "Specialized exercise programs to improve strength, flexibility and posture which can, in turn, help to reduce pain.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "Complementary Therapies",
                    "desc": "In this technique, acupuncture, massage therapy, and cognitive behavioral therapy (CBT) are utilized to complement conventional pain management strategies.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Interventional Procedures",
                    "desc": "Those might involve nerve ablations or implantable devices, such as spinal cord stimulators, that disrupt pain signals.",
                    "icon": "fa-solid fa-procedures"
                },
                {
                    "title": "Nerve Stimulation",
                    "desc": "You have TENS or spinal cord stimulators to interrupt nerve signals and lessen chronic pain.",
                    "icon": "fa-solid fa-bolt"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "From just easing the pain, effective pain management brings ample benefits. It can change your life, body, and mind.",
            "items": [
                {
                    "title": "Enhanced Movement and Ability",
                    "desc": "Pain relief enables patients to move more freely and participate in everyday life.",
                    "icon": "fa-solid fa-walking"
                },
                {
                    "title": "Enhanced Quality of Life",
                    "desc": "Less pain means better mental and emotional states and an overall greater quality of life.",
                    "icon": "fa-solid fa-smile"
                },
                {
                    "title": "Personalized Care",
                    "desc": "Every treatment program is customized to the underlying cause and type of pain that you are experiencing.",
                    "icon": "fa-solid fa-user-md"
                },
                {
                    "title": "Non-Surgical Solutions",
                    "desc": "Many methods of pain management — injections and physical therapy, for example — provide alternatives to surgery.",
                    "icon": "fa-solid fa-scissors"
                },
                {
                    "title": "Long-Term Relief",
                    "desc": "Targeted therapies give long-lasting results because they treat the underlying driver of the pain rather than just the symptoms.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Pain management is indicated in a broad group of patients, depending on the etiology and severity of their pain. Before receiving any treatment, a proper assessment should be done that can lead to a favorable outcome.",
            "items": [
                {
                    "title": "Chronic Pain Conditions",
                    "desc": "Have chronic pain conditions like arthritis, fibromyalgia, or back pain.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "Injuries",
                    "desc": "A sprain, fracture, or sports-related injury.",
                    "icon": "fa-solid fa-bone"
                },
                {
                    "title": "Post-Surgical Recovery",
                    "desc": "Are recovering from surgical treatment and require help with their post-operative discomfort.",
                    "icon": "fa-solid fa-procedures"
                },
                {
                    "title": "Neuropathic Pain",
                    "desc": "Suffer neuropathic pain caused by nerve damage or diseases such as diabetes.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "Non-Surgical Candidates",
                    "desc": "Are seeking non-surgical solutions to control pain.",
                    "icon": "fa-solid fa-ban"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "When preparing for pain management treatments, there are a few steps you can take to ensure the procedures are safe and effective.",
            "items": [
                {
                    "title": "Consultation",
                    "desc": "Consulting with your physician about your pain, medical history, and medications you are taking.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Diagnostic Testing",
                    "desc": "Finish any imaging studies or any tests that you may need to have to accurately identify what's causing your pain.",
                    "icon": "fa-solid fa-x-ray"
                },
                {
                    "title": "Lifestyle Adjustments",
                    "desc": "Adhere to any lifestyle changes your healthcare provider recommends, including dietary modifications, exercise or stress management.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "Medication Review",
                    "desc": "If you're using medications, which includes over-the-counter drugs or supplements, let your doctor know to prevent any possible interactions.",
                    "icon": "fa-solid fa-pills"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "While the effectiveness of pain management improves, the outcomes vary based on the treatment method and the person's condition, but the overall aim is to provide considerable and enduring pain relief.",
            "items": [
                {
                    "title": "Reduced Pain Levels",
                    "desc": "Most patients experience a dramatic reduction in pain intensity.",
                    "icon": "fa-solid fa-chart-line-down"
                },
                {
                    "title": "Enhanced Mobility",
                    "desc": "They enable patients to move more easily through physical therapies and interventions.",
                    "icon": "fa-solid fa-wheelchair"
                },
                {
                    "title": "Increased Activity Levels",
                    "desc": "Pain management allows patients to resume their normal daily activities and participate in physical activities that they may have previously avoided.",
                    "icon": "fa-solid fa-running"
                },
                {
                    "title": "Improved Mental Health",
                    "desc": "In relieving pain, patients are usually alleviated from the burden of chronic pain-related emotional issues, like anxiety and depression.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "Sustainable Pain Relief",
                    "desc": "Most patients sustained long-term relief and enhanced quality of life with a customized treatment plan.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Why Choose Elite Body Home for Pain Management?",
            "subheading": "",
            "para": "At Elite Body Home, our practitioners are skilled in providing individualized treatment tailored to alleviate your pain. Our expert therapists utilize the latest technology and evidence-based treatments techniques including injection therapies, physiotherapy and dry needling as well as custom-tailored comprehensive treatment plans just for you. Our method is comprehensive—we treat the physical as well as the mental component of pain, with your health at top of our mind. Our modern facility and focused approach to rehabilitation will help you take back your life and find new ways to enjoy your life.",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long does pain management treatment take to work?",
                    "answer": "The time varies depending on the treatment method and individual condition. Some patients experience relief immediately after injections, while others may need several weeks of physical therapy."
                },
                {
                    "question": "Are pain management injections painful?",
                    "answer": "Most injections involve minimal discomfort. We use local anesthesia to numb the area before administering the injection."
                },
                {
                    "question": "How often can I receive pain management treatments?",
                    "answer": "Frequency depends on your specific condition and treatment plan. Some therapies like physical therapy may be done several times a week, while injections are typically spaced weeks or months apart."
                },
                {
                    "question": "Will I need to continue medications after pain management treatment?",
                    "answer": "This depends on your condition. Many patients can reduce or eliminate medications after successful pain management, while others may need ongoing medication at lower doses."
                }
            ]
        }
    },
    "ar": {
        "url": "pain-management",
        "meta": {
            "title": "إدارة الألم في دبي | ايليت بودي هوم",
            "description": "حلول شاملة لإدارة الألم المزمن والإصابات وآلام ما بعد الجراحة. علاجات متقدمة مصممة خصيصًا لاحتياجاتك.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/ar/services/pain-management/",
            "og": {
                "title": "إدارة الألم في دبي | ايليت بودي هوم",
                "description": "حلول متخصصة لتخفيف الألم وتحسين جودة الحياة",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Pain-Management-Dubai.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "إدارة الألم"],
        "hero": {
            "title": "إدارة الألم",
            "subtitle": "علاج احترافي لتخفيف الألم وتحسين جودة الحياة",
            "description": "تُعد إدارة الألم جزءاً أساسياً من الرعاية الصحية، وتهدف إلى تخفيف الألم، وتحسين جودة الحياة، وتمكين المرضى من الحركة وممارسة الأنشطة اليومية. سواء كنت تعاني من ألم مزمن، أو ألم ناتج عن إصابة، أو ألم ما بعد الجراحة، فإن الإدارة المتخصصة للألم يمكن أن تُحدث فرقاً كبيراً في حياتك اليومية، سواء كنت مريضاً، أو تعمل في الرعاية الصحية، أو بين الاثنين."
        },
        "howItWorks": {
            "title": "ما هي إدارة الألم؟",
            "description": "إدارة الألم هي تخصص فرعي في الطب، يركز على تخفيف الألم والتحكم به للارتقاء بالصحة الجسدية والنفسية للمريض. يعمل فريقنا الطبي المتخصص من خلال نهج شامل، يعتمد على استخدام مجموعة متكاملة من طرق العلاج الطبي المتقدم وخطط الرعاية الشخصية والتقنيات العلاجية التي تهدف إلى التحكم بالألم وتخفيفه.",

        },
        "procedureSteps": {
            "heading": "تقنيات إدارة الألم في ايليت بودي هوم",
            "para": "نستخدم مجموعة من أحدث الأساليب الطبية المصممة خصيصاً لتوفير راحة فعالة تتناسب مع حالة كل مريض:",
            "steps": [
                {
                    "title": "الأدوية",
                    "desc": "تشمل مسكنات الألم التي لا تتطلب وصفة طبية، والأدوية التي تتطلب وصفة طبيب مثل مرخيات العضلات، أو الأفيونات، أو الأدوية المضادة للالتهاب.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "الإبر",
                    "desc": "يتضمن إبر الكورتيكوستيرويد، وإبر إحصار العصب، أو إبر نقطة الزناد لتقليل الالتهاب وتخفيف الألم في مناطق محددة من الجسم.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "العلاج الطبيعي",
                    "desc": "تُصمم برامج تمارين علاجية لتحسين القوة والمرونة وتعديل وضعية الجسم، مما يساهم في تقليل الشعور بالألم.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "العلاج التكميلي",
                    "desc": "تُستخدم طرق علاج مختلفة مثل الوخز بالإبر، والتدليك العلاجي، والعلاج المعرفي السلوكي (CBT)، لدعم أساليب إدارة الألم التقليدية.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "الإجراءات التداخلية",
                    "desc": "قد تشمل هذه الإجراءات كي الأعصاب أو زراعة أجهزة مثل محفزات الحبل الشوكي، والتي تعمل على تعطيل إشارات الألم.",
                    "icon": "fa-solid fa-procedures"
                },
                {
                    "title": "تحفيز الأعصاب",
                    "desc": "تُستخدم تقنيات مثل التحفيز الكهربائي للأعصاب عبر الجلد أو محفزات الحبل الشوكي لقطع الإشارات العصبية وتقليل الألم المزمن.",
                    "icon": "fa-solid fa-bolt"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "",
            "para": "لا تقتصر إدارة الألم على التخفيف من الأعراض فحسب، بل تُحدث فرقاً حقيقياً في حياة المريض على الصحة الجسدية والنفسية. ومن أبرز فوائدها:",
            "items": [
                {
                    "title": "تحسين الحركة والقدرة الجسدية",
                    "desc": "يصبح المريض قادراً على التحرك بسهولة أكبر مع تراجع الألم، والمشاركة في الأنشطة اليومية دون عوائق.",
                    "icon": "fa-solid fa-walking"
                },
                {
                    "title": "تحسين جودة الحياة",
                    "desc": "ينعكس انخفاض مستوى الألم إيجابياً على الحالة النفسية والذهنية، مما يعزز الإحساس بالراحة العامة والسعادة.",
                    "icon": "fa-solid fa-smile"
                },
                {
                    "title": "رعاية مخصصة لحالتك",
                    "desc": "تُصمم كل خطة علاجية بناءً على السبب الرئيسي للألم ونوعه.",
                    "icon": "fa-solid fa-user-md"
                },
                {
                    "title": "بدائل غير جراحية",
                    "desc": "توفر بعض تقنيات إدارة الألم، مثل الإبر والعلاج الطبيعي، حلولاً فعالة دون الحاجة إلى التدخل الجراحي.",
                    "icon": "fa-solid fa-scissors"
                },
                {
                    "title": "راحة طويلة الأمد",
                    "desc": "يؤدي العلاج إلى نتائج دائمة وليست مؤقتة؛ لإنه يستهدف السبب الأساسي للألم.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "يُستخدم علاج إدارة الألم لمجموعة واسعة من المرضى، ويعتمد ذلك على سبب الألم وشدته. وقبل البدء بأي علاج، يجب إجراء تقييم شامل لضمان الحصول على نتائج فعالة وآمنة.",
            "items": [
                {
                    "title": "حالات الألم المزمن",
                    "desc": "إذا كان يُعاني من حالات ألم مزمن مثل التهاب المفاصل، أو الفيبروميالجيا، أو آلام الظهر.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "الإصابات",
                    "desc": "إذا كان تعرض لالتواء، أو كسر، أو إصابة ناتجة عن ممارسة الرياضة.",
                    "icon": "fa-solid fa-bone"
                },
                {
                    "title": "التعافي بعد الجراحة",
                    "desc": "إذا كان في مرحلة التعافي بعد إجراء جراحي ويحتاج إلى تخفيف الألم بعد العملية.",
                    "icon": "fa-solid fa-procedures"
                },
                {
                    "title": "آلام الأعصاب",
                    "desc": "إذا كان يُعاني من آلام الأعصاب الناجمة عن تلف الأعصاب أو أمراض مثل السكري.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "المرضى غير المرشحين للجراحة",
                    "desc": "إذا كان يبحث عن حلول غير جراحية للسيطرة على الألم وتحسين جودة حياته.",
                    "icon": "fa-solid fa-ban"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "لضمان فاعلية وأمان جلسات إدارة الألم، يجب اتباع بعض الخطوات التحضيرية الأساسية:",
            "items": [
                {
                    "title": "الاستشارة",
                    "desc": "ابدأ بمناقشة حالتك الصحية مع الطبيب، بما في ذلك تفاصيل الألم الذي تعاني منه، وتاريخك الطبي، والأدوية التي تتناولها.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "الفحوصات التشخيصية",
                    "desc": "أكمل أي فحوصات أو إشاعات يطلبها الطبيب لتحديد السبب الدقيق للألم.",
                    "icon": "fa-solid fa-x-ray"
                },
                {
                    "title": "تعديلات في نمط الحياة",
                    "desc": "التزم بأي تغييرات في نمط الحياة يوصي بها الطبيب، بما في ذلك تعديل النظام الغذائي، وممارسة التمارين الرياضية، والتحكم في التوتر.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "مراجعة الأدوية",
                    "desc": "إذا كنت تستخدم أي أدوية، سواء كانت بوصفة طبية أو بدون وصفة أو مكملات غذائية، احرص على إبلاغ طبيبك لتفادي أي تداخلات محتملة.",
                    "icon": "fa-solid fa-pills"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "مع تحسن فاعلية تقنيات إدارة الألم، قد تختلف النتائج باختلاف نوع العلاج وحالة المريض، إلا أن الهدف الأساسي يبقى تحقيق راحة ملحوظة وطويلة الأمد من الألم.",
            "items": [
                {
                    "title": "انخفاض مستويات الألم",
                    "desc": "يشعر معظم المرضى بانخفاض كبير في شدة الألم بعد بدء العلاج.",
                    "icon": "fa-solid fa-chart-line-down"
                },
                {
                    "title": "تحسن القدرة على الحركة",
                    "desc": "تُسهم برامج العلاج الفيزيائي والتداخلي المناسبة في تسهيل الحركة وتحقيق مرونة أكبر.",
                    "icon": "fa-solid fa-wheelchair"
                },
                {
                    "title": "زيادة النشاط اليومي",
                    "desc": "تمكن إدارة الألم المرضى من استئناف أنشطتهم اليومية والبدنية التي كانوا يتجنبونها سابقًا.",
                    "icon": "fa-solid fa-running"
                },
                {
                    "title": "تحسن الصحة النفسية",
                    "desc": "ينعكس تخفيف الألم إيجابيًا على الحالة النفسية، ويُقلل من التوتر والقلق المرتبطين بالألم المزمن.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "راحة تدوم طويلًا",
                    "desc": "يتمكن معظم المرضى من تحقيق راحة طويلة المدى وتحسين جودة الحياة مع الخطط العلاجية المخصصة.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "aftercareTips": {
            "heading": "لماذا تختار ايليت بودي هوم لإدارة الألم؟",
            "subheading": "",
            "para": "يتمتع فريقنا من الأطباء المتخصصين في ايليت بودي هوم بخبرة عالية في تقديم خطط علاجية مخصصة تهدف إلى تخفيف الألم. نستخدم أحدث التقنيات الطبية وطرق العلاج المعتمدة علميًا مثل العلاج بالإبر، والعلاج الطبيعي، وتقنية الوخز بالإبر، إلى جانب برامج علاجية شاملة تُصمم خصيصًا وفقًا لاحتياجاتك الفردية. نعتمد نهجًا شاملاً لا يركز فقط على الألم الجسدي، بل يأخذ بعين الاعتبار العوامل النفسية المرتبطة به، لضمان رعاية صحية متكاملة. نساعدك في عيادتنا المتطورة على استعادة نمط حياتك الطبيعي، والعودة إلى أنشطتك اليومية بثقة وراحة.",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة المكررة",
            "subheading": "",
            "items": [
                {
                    "question": "كم من الوقت يستغرق علاج إدارة الألم ليعمل؟",
                    "answer": "يختلف الوقت حسب طريقة العلاج والحالة الفردية. بعض المرضى يشعرون بالراحة فورًا بعد الحقن، بينما قد يحتاج آخرون لعدة أسابيع من العلاج الطبيعي."
                },
                {
                    "question": "هل حقن إدارة الألم مؤلمة؟",
                    "answer": "معظم الحقن تتضمن حد أدنى من الانزعاج. نستخدم مخدر موضعي لتخدير المنطقة قبل الحقن."
                },
                {
                    "question": "كم مرة يمكنني تلقي علاجات إدارة الألم؟",
                    "answer": "يعتمد التكرار على حالتك الخاصة وخطة العلاج. بعض العلاجات مثل العلاج الطبيعي قد تكون عدة مرات أسبوعيًا، بينما الحقن عادةً ما تكون متباعدة بأسابيع أو أشهر."
                },
                {
                    "question": "هل سأحتاج لمواصلة الأدوية بعد علاج إدارة الألم؟",
                    "answer": "هذا يعتمد على حالتك. العديد من المرضى يمكنهم تقليل أو إيقاف الأدوية بعد نجاح العلاج، بينما قد يحتاج آخرون لمواصلة الأدوية بجرعات أقل."
                }
            ]
        }
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Rehabilitations-scaled.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 125,
            "title": "Pain Management in Dubai",
            "description": "Comprehensive pain relief solutions for chronic pain, injuries and post-surgical recovery",
            "price": 1200
        }
    }
}